import {
  AtsCandidateFilter,
  DataTableFilter,
  FilterType,
} from 'generated/resources/datatable.graphql'
import {
  AtsCandidateJobPostingFilterOptionFragment,
  AtsCandidateLegalEntityFilterOptionFragment,
  AtsCandidateLocationFilterOptionFragment,
  AtsCandidateRejectionReasonFilterOptionFragment,
  AtsCandidateSourceFilterOptionFragment,
  AtsCandidateTagFilterOptionFragment,
  AtsCandidateTeamFilterOptionFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

export interface CandidateFilterOptions {
  candidateSources: AtsCandidateSourceFilterOptionFragment[]
  jobPostings: AtsCandidateJobPostingFilterOptionFragment[]
  legalEntities: AtsCandidateLegalEntityFilterOptionFragment[]
  locationEntities: AtsCandidateLocationFilterOptionFragment[]
  rejectionReasons: AtsCandidateRejectionReasonFilterOptionFragment[]
  tags: AtsCandidateTagFilterOptionFragment[]
  teams: AtsCandidateTeamFilterOptionFragment[]
}

export const candidateFilters = (options: CandidateFilterOptions) => {
  const allOptions: DataTableFilter<AtsCandidateFilter>[] = [
    {
      identifier: 'candidateSearches.statusFilter',
      title: i18n.t('ats.candidates.filters.status'),
      path: 'status',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: i18n.t('ats.candidates.filters.status_options.active'),
              value: 'active',
            },
            {
              label: i18n.t('ats.candidates.filters.status_options.talent_pool'),
              value: 'talent_pool',
            },
            {
              label: i18n.t('ats.candidates.filters.status_options.archived'),
              value: 'archived',
            },
          ],
        },
      ],
      default: true,
    },
    {
      identifier: 'candidateSearches.jobPostingFilter',
      title: i18n.t('ats.candidates.filters.jobs'),
      path: 'jobPostingId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.jobPostings.map((jobPosting) => ({
            value: jobPosting.id,
            label: jobPosting.title,
          })),
        },
      ],
      default: true,
    },
    {
      identifier: 'candidateSearches.tagsFilter',
      title: i18n.t('ats.candidates.filters.tags'),
      path: 'tagIds',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.tags.map((tag) => ({ value: tag.id, label: tag.name })),
        },
      ],
      default: true,
    },
    {
      identifier: 'candidateSearches.sourceFilter',
      title: i18n.t('ats.candidates.filters.source'),
      path: 'sourceId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.candidateSources.map((s) => ({
            value: s.id,
            label: getSourceLabel(s.name),
          })),
        },
      ],
      default: true,
    },
    {
      identifier: 'candidateSearches.locationFilter',
      title: i18n.t('ats.candidates.filters.location'),
      path: 'jobPostingLocationId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.locationEntities.map((location) => ({
            value: location.id,
            label: location.name,
          })),
        },
      ],
    },
    {
      identifier: 'candidateSearches.rejectionReasonFilter',
      title: i18n.t('ats.candidates.filters.rejection_reason'),
      path: 'rejectionReasonId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.rejectionReasons.map((rejectionReason) => ({
            value: rejectionReason.id,
            label: rejectionReason.reason,
          })),
        },
      ],
    },
    {
      identifier: 'candidateSearches.ratingFilter',
      title: i18n.t('ats.candidates.filters.rating'),
      path: 'ratingAverage',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'SingleChoice',
          operation: 'gteq',
          options: [
            {
              label: i18n.t('ats.candidates.filters.rating_options.excellent'),
              value: 5,
            },
            {
              label: i18n.t('ats.candidates.filters.rating_options.good'),
              value: 4,
            },
            {
              label: i18n.t('ats.candidates.filters.rating_options.average'),
              value: 3,
            },
          ],
        },
      ],
    },
    {
      identifier: 'candidateSearches.legalEntityFilter',
      title: i18n.t('ats.candidates.filters.legal_entity'),
      path: 'jobPostingLegalEntityId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.legalEntities.flatMap((legalEntity) =>
            !legalEntity.legalName
              ? []
              : [
                  {
                    value: legalEntity.id,
                    label: legalEntity.legalName,
                  },
                ]
          ),
        },
      ],
    },
    {
      identifier: 'candidateSearches.teamFilter',
      title: i18n.t('ats.candidates.filters.team'),
      path: 'jobPostingTeamId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: options.teams.map((team) => ({
            value: team.id,
            label: team.name,
          })),
        },
      ],
    },
    {
      identifier: 'candidateSearches.consentFilter',
      title: i18n.t('ats.candidates.filters.consent'),
      path: 'consent',
      type: FilterType.String,
      categories: [
        {
          kind: 'SingleChoice',
          operation: 'eq',
          options: [
            {
              label: i18n.t('ats.candidates.filters.consent_options.not_given'),
              value: 'no_consent_given',
            },
            {
              label: i18n.t('ats.candidates.filters.consent_options.not_expired'),
              value: 'not_expired',
            },
            {
              label: i18n.t('ats.candidates.filters.consent_options.expired'),
              value: 'expired',
            },
          ],
        },
      ],
    },
  ]

  return allOptions
}
